<template>
    <div class="phone-input" :class="validationClasses">
        <div ref="phoneInputRef">
            <v-row dense>
                <v-col cols="auto">
                    <v-autocomplete
                        v-model="countryCode"
                        v-model:search="search"
                        :items="countryCodes"
                        :variant="variant"
                        :color="color"
                        :disabled="isDisabled"
                        :readonly="isReadonly"
                        :error="isValid === false"
                        :custom-filter="filterByPartialCountryCode"
                        :menu-props="menuProps"
                        :bg-color="bgColor"
                        v-model:focused="focusedCountryCode"
                        hide-details
                        type="tel"
                        autocomplete="tel-country-code"
                        inputmode="tel"
                        item-title="dial_code"
                        item-value="code"
                        menu-icon=""
                        eager
                        auto-select-first
                        class="phone-input__code"
                    >
                        <template #item="{ props: listItemProps, item }">
                            <v-list-item v-bind="listItemProps" title="" class="phone-input__code-item">
                                <v-row class="flex-nowrap">
                                    <v-col cols="auto" class="phone-input__code-item-code">
                                        <span class="text-h6">{{ item.raw.dial_code }}</span>
                                    </v-col>
                                    <v-col cols="auto" class="phone-input__code-item-name text-truncate flex-grow-1">
                                        <span class="text-h6 font-weight-regular text-no-wrap">{{ item.raw.name }}</span>
                                    </v-col>
                                </v-row>
                            </v-list-item>
                        </template>
                    </v-autocomplete>
                </v-col>
                <v-col>
                    <v-text-field
                        v-model="number"
                        :variant="variant"
                        :color="color"
                        :autofocus="autofocus"
                        :disabled="isDisabled"
                        :readonly="isReadonly"
                        :error="isValid === false"
                        v-model:focused="focused"
                        :bg-color="bgColor"
                        :placeholder="placeholder"
                        :label="label"
                        :id="id"
                        hide-details
                        type="tel"
                        autocomplete="tel-national"
                        inputmode="tel"
                        class="phone-input__number"
                    />
                </v-col>
            </v-row>
        </div>
        <div v-if="hasDetails" class="phone-input__details v-input__details">
            <v-messages :id="messagesId" :active="hasMessages" :messages="messages" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { useElementSize } from '@vueuse/core';
import { onBeforeMount, onBeforeUnmount, onMounted, ref, unref, shallowRef, nextTick, watch, computed, useId } from 'vue';
// @ts-ignore
import { getCurrentInstance, wrapInArray } from 'vuetify/lib/util/index.mjs';
// @ts-ignore
import { useForm } from 'vuetify/lib/composables/form.mjs';
// @ts-ignore
import { useToggleScope } from 'vuetify/lib/composables/toggleScope.mjs';
import countryCodes from './countrycodes.json';
import { useViewportSize } from '../../composables/useViewportSize';
import { useDisplay } from 'vuetify';
import type { VTextField } from 'vuetify/components';

export interface Model {
    countryCode: string;
    number: string;
}

interface Props {
    id?: string;
    name?: string;
    autofocus?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    hideDetails?: boolean | string;
    error?: boolean;
    errorMessages?: string[];
    validateOn?: NonNullable<InstanceType<typeof VTextField>['$props']['validateOn']>;
    maxErrors?: number;
    validationValue?: any;
    rules?: NonNullable<InstanceType<typeof VTextField>['$props']['rules']>;
    color?: string;
    bgColor?: string;
    placeholder?: string;
    label?: string;
    variant?: NonNullable<InstanceType<typeof VTextField>['$props']['variant']>;
    hint?: string;
    persistentHint?: boolean;
    messages?: string | readonly string[];
}

interface Emits {
    (e: 'update:modelValue', value: Model): void;
    (e: 'update:focused', value: boolean): void;
    (e: 'update:focusedCountryCode', value: boolean): void;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: () => ({ countryCode: '', number: '' }),
    maxErrors: 1,
    rules: () => [],
    variant: 'outlined'
});
defineEmits<Emits>();
const phoneInputRef = ref<HTMLDivElement>();
const modelValue = defineModel<Model>('modelValue', { required: true });
const focused = defineModel<boolean>('focused');
const focusedCountryCode = defineModel<boolean>('focusedCountryCode');
const countryCode = computed({
    get() {
        return countryCodes.find(code => code.dial_code === modelValue.value.countryCode)?.code || '';
    },
    set(value) {
        const country = countryCodes.find(code => code.code === value);
        modelValue.value = { ...modelValue.value, countryCode: country?.dial_code || '' };
    }
});
const number = computed({
    get() {
        return modelValue.value.number;
    },
    set(value) {
        modelValue.value = { ...modelValue.value, number: value };
    }
});
const { mobile: isMobile } = useDisplay();
const search = ref('');

watch(
    modelValue,
    value => {
        search.value = value.countryCode;
    },
    { immediate: true }
);

function filterByPartialCountryCode(value: string, query: string) {
    return !query || value.includes(query);
}

const { height } = useViewportSize();
const { width } = useElementSize(phoneInputRef, undefined, { box: 'border-box' });
const menuProps = computed<any>(() => ({
    width: width.value,
    maxWidth: width.value,
    minHeight: 40,
    maxHeight: isMobile.value ? Math.max(0, Math.min(height.value - 150, 300)) : 300,
    rounded: true,
    contentClass: 'phone-input__dropdown',
    location: 'bottom left',
    offset: [4, 0]
}));

const componentName = 'phone-input';
const validationModel = computed<Model>(() => (props.validationValue === undefined ? modelValue.value : props.validationValue));
const form = useForm();
const internalErrorMessages = ref<string[]>([]);
const isPristine = shallowRef(true);
const isDirty = computed(
    () =>
        !!(
            wrapInArray(
                `${modelValue.value.countryCode || ''}${modelValue.value.number || ''}` === ''
                    ? null
                    : `${modelValue.value.countryCode || ''}${modelValue.value.number || ''}`
            ).length ||
            wrapInArray(
                validationModel.value.countryCode === '' || validationModel.value.number === ''
                    ? null
                    : `${validationModel.value.countryCode || ''}${validationModel.value.number || ''}`
            ).length
        )
);
const isValidating = shallowRef(false);
const validationClasses = computed(() => {
    return {
        [`${componentName}--error`]: isValid.value === false,
        [`${componentName}--dirty`]: isDirty.value,
        [`${componentName}--disabled`]: isDisabled.value,
        [`${componentName}--readonly`]: isReadonly.value
    };
});
const isDisabled = computed(() => !!(props.disabled ?? form?.isDisabled.value));
const isReadonly = computed(() => !!(props.readonly ?? form?.isReadonly.value));
const errorMessages = computed<string[]>(() => {
    return props.errorMessages?.length
        ? wrapInArray(props.errorMessages).concat(internalErrorMessages.value).slice(0, Math.max(0, +props.maxErrors))
        : internalErrorMessages.value;
});
const validateOn = computed(() => {
    let value = (props.validateOn ?? form?.validateOn?.value) || 'input';
    if (value === 'lazy') value = 'input lazy';
    if (value === 'eager') value = 'input eager';
    const set = new Set(value?.split(' ') ?? []);

    return {
        input: set.has('input'),
        blur: set.has('blur') || set.has('input') || set.has('invalid-input'),
        invalidInput: set.has('invalid-input'),
        lazy: set.has('lazy'),
        eager: set.has('eager')
    };
});
const isValid = computed(() => {
    if (props.error || props.errorMessages?.length) return false;
    if (!props.rules.length) return true;
    if (isPristine.value) {
        return internalErrorMessages.value.length || validateOn.value.lazy ? null : true;
    } else {
        return !internalErrorMessages.value.length;
    }
});
const vm = getCurrentInstance('validation');
const uid = computed(() => props.name ?? unref(useId()));
const id = computed(() => props.id || `input-${uid.value}`);
const messagesId = computed(() => `${id.value}-messages`);
const messages = computed(() => {
    if (props.errorMessages?.length || (!isPristine.value && errorMessages.value.length)) {
        return errorMessages.value;
    } else if (props.hint && (props.persistentHint || focused.value)) {
        return props.hint;
    }

    return props.messages || [];
});
const hasMessages = computed(() => !!messages.value?.length);
const hasDetails = computed(() => !props.hideDetails || (props.hideDetails === 'auto' && hasMessages));

useToggleScope(
    () => validateOn.value.input || (validateOn.value.invalidInput && isValid.value === false),
    () => {
        watch(validationModel, () => {
            if (validationModel.value != null) {
                validate();
            } else if (focused.value) {
                const unwatch = watch(focused, val => {
                    if (!val) validate();

                    unwatch();
                });
            }
        });
    }
);

useToggleScope(
    () => validateOn.value.blur,
    () => {
        watch(focused, val => {
            if (!val) validate();
        });
    }
);

watch([isValid, errorMessages], () => {
    form?.update?.(uid.value, isValid.value, errorMessages.value);
});

async function reset() {
    modelValue.value = { countryCode: '', number: '' };
    search.value = modelValue.value.countryCode;
    await nextTick();
    await resetValidation();
}

async function resetValidation() {
    isPristine.value = true;
    if (!validateOn.value.lazy) {
        await validate(!validateOn.value.eager);
    } else {
        internalErrorMessages.value = [];
    }
}

async function validate(silent = false) {
    const results = [];

    isValidating.value = true;

    for (const rule of props.rules) {
        if (results.length >= +(props.maxErrors ?? 1)) {
            break;
        }

        const handler = typeof rule === 'function' ? rule : () => rule;
        const result = await handler(validationModel.value);

        if (result === true) continue;

        if (result !== false && typeof result !== 'string') {
            // eslint-disable-next-line no-console
            console.warn(`${result} is not a valid value. Rule functions must return boolean true or a string.`);

            continue;
        }

        results.push(result || '');
    }

    internalErrorMessages.value = results;
    isValidating.value = false;
    isPristine.value = silent;

    return internalErrorMessages.value;
}

onBeforeMount(() => {
    form?.register?.({ id: uid.value, vm, validate, reset, resetValidation });
});

onBeforeUnmount(() => {
    form?.unregister?.(uid.value);
});

onMounted(async () => {
    if (!validateOn.value.lazy) {
        await validate(!validateOn.value.eager);
    }
    form?.update?.(uid.value, isValid.value, errorMessages.value);
});
</script>

<style lang="scss">
@use 'vuetify/settings' as *;
@use 'vuetify/lib/styles/tools/_index' as *;

.phone-input {
    &__code,
    &__number {
        .v-field__input {
            font-weight: 500;
        }
    }

    &__code {
        &.v-autocomplete .v-autocomplete__selection-text {
            text-overflow: initial;
        }

        .v-field__input {
            width: 85px;
        }

        &-item-code {
            min-width: 85px;
        }
    }

    &__details {
        padding-inline: 16px;
    }

    &__dropdown {
        border: 1px solid rgb(var(--v-theme-primary));
        position: absolute;
        top: calc(100% + 6px);
        left: -1px;
        min-width: 320px !important;

        .v-list {
            padding-top: 0;
            padding-bottom: 0;
        }

        .v-list-item {
            border-bottom: 1px solid var(--v-color-grey-lighten-1);

            &:first-of-type {
                border-top: 1px solid var(--v-color-grey-lighten-1);
            }
        }
    }

    &--error {
        .v-input__details .v-messages {
            opacity: 1;
        }

        &:not(.phone-input--disabled) .v-input__details .v-messages {
            color: rgb(var(--v-theme-error));
        }
    }
}
</style>
